import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bed, Maximize, Layers } from "lucide-react";
import SEO from "@/components/SEO";

const PRODUCTS = [
  {
    slug: "t1",
    name: "T1",
    subtitle: "Studio compact avec mezzanine",
    surface: "13 m\u00B2",
    capacity: "Banquette + mezzanine",
    structure: "CLT 60 mm",
    image: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/bm9vithu_T1.png",
    link: "/produits/t1",
    available: true,
  },
  {
    slug: "t2",
    name: "T2",
    subtitle: "Studio familial avec mezzanine",
    surface: "13 m\u00B2",
    capacity: "4 couchages",
    structure: "CLT 60 mm",
    image: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/lz3rr73o_ChatGPT%20Image%2025%20avr.%202026%20a%CC%80%2017_22_05.png",
    link: "/produits/t2",
    available: true,
  },
  {
    slug: "t3",
    name: "T3",
    subtitle: "4 couchages plein pied",
    surface: "13 m\u00B2",
    capacity: "4 couchages",
    structure: "CLT 60 mm",
    image: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/2bq6f9k3_T3.png",
    link: "/produits/t3",
    available: true,
  },
];

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function ProductsPage() {
  return (
    <div data-testid="products-page" className="pt-20">
      {/* Hero */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">La gamme</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-soft-black mt-4 mb-6">
              Nos modules bois
            </h1>
            <p className="font-body text-base md:text-lg text-soft-black/60 leading-relaxed max-w-2xl">
              Trois formats d'habitats bois concus avec la même exigence de qualité, de confort et de durabilité.
              Choisissez le module adapté à votre projet.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Products Grid */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {PRODUCTS.map((product, i) => (
              <Reveal key={product.slug} delay={i * 120}>
                <div className="bg-off-white h-full flex flex-col border border-warm-grey/20">
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden bg-light-beige">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Bientot disponible</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-8 md:p-10 flex flex-col flex-1">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-natural-wood">YONO</span>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-soft-black mt-2 mb-2">{product.name}</h2>
                    <p className="font-body text-sm text-soft-black/60 mb-6">{product.subtitle}</p>

                    {/* Specs mini */}
                    <div className="grid grid-cols-3 gap-px bg-warm-grey/20 border border-warm-grey/20 mb-6">
                      <div className="bg-off-white p-3 text-center">
                        <Bed className="h-4 w-4 text-natural-wood mx-auto mb-1.5" strokeWidth={1.5} />
                        <span className="font-mono text-[10px] uppercase tracking-wider text-soft-black/40 block">Capacite</span>
                        <span className="font-body text-xs text-soft-black font-medium">{product.capacity}</span>
                      </div>
                      <div className="bg-off-white p-3 text-center">
                        <Maximize className="h-4 w-4 text-natural-wood mx-auto mb-1.5" strokeWidth={1.5} />
                        <span className="font-mono text-[10px] uppercase tracking-wider text-soft-black/40 block">Surface</span>
                        <span className="font-body text-xs text-soft-black font-medium">{product.surface}</span>
                      </div>
                      <div className="bg-off-white p-3 text-center">
                        <Layers className="h-4 w-4 text-natural-wood mx-auto mb-1.5" strokeWidth={1.5} />
                        <span className="font-mono text-[10px] uppercase tracking-wider text-soft-black/40 block">Structure</span>
                        <span className="font-body text-xs text-soft-black font-medium">{product.structure}</span>
                      </div>
                    </div>

                    {/* Tarif */}
                    <div className="mt-auto">
                      <div className="mb-6">
                        <span className="font-mono text-xs uppercase tracking-wider text-soft-black/40">Tarif</span>
                        <div data-testid={`products-${product.slug}-price`} className="font-heading text-2xl tracking-tight text-soft-black mt-1">Prix sur demande</div>
                      </div>

                      {product.available ? (
                        <Link
                          to={product.link}
                          data-testid={`products-${product.slug}-link`}
                          className="flex items-center justify-center gap-2 bg-soft-black text-off-white px-6 py-3.5 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300 w-full"
                        >
                          En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      ) : (
                        <Link
                          to="/contact"
                          data-testid={`products-${product.slug}-link`}
                          className="flex items-center justify-center gap-2 border border-soft-black text-soft-black px-6 py-3.5 font-body text-xs uppercase tracking-[0.15em] hover:bg-soft-black hover:text-off-white transition-colors duration-300 w-full"
                        >
                          Nous contacter <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mb-4">
              Besoin d'aide pour choisir ?
            </h2>
            <p className="font-body text-soft-black/60 mb-8">
              Notre équipé vous accompagne pour identifiér le module le plus adapté à votre projet.
            </p>
            <Link to="/contact" data-testid="products-contact-btn"
              className="inline-flex items-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300">
              Nous contacter <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
